import { ApiError, DuplicateTicketApiError } from '@jq4race/api-client';

import type { OutboxLastError } from './outbox-item.js';

export type SyncErrorClassification =
  | { kind: 'retry'; error: OutboxLastError }
  | { kind: 'needs-auth'; error: OutboxLastError }
  | { kind: 'reject'; error: OutboxLastError };

/** 403 codes that mean "this item's own location was denied" (see location-access-interceptor.ts). */
const LOCATION_DENIED_CODES = new Set(['LOCATION_ACCESS_DENIED', 'LOCATION_INACTIVE']);

/** 403 code from SubscriptionAccessGuard (backend) — also what the outbox item's `lastError.code` carries. */
export const SUBSCRIPTION_BLOCKED_CODE = 'subscription_access_blocked';

/**
 * True when an outbox item is waiting (not terminally rejected) because the
 * organization's subscription is suspended/canceled. Drives the dedicated
 * "ask your administrator" message instead of a generic retry spinner.
 */
export function isBlockedBySubscription(item: {
  state: string;
  lastError?: OutboxLastError | null;
}): boolean {
  return (
    (item.state === 'pending' || item.state === 'syncing') &&
    item.lastError?.code === SUBSCRIPTION_BLOCKED_CODE
  );
}

function toLastError(err: ApiError): OutboxLastError {
  const field = (err.details as { field?: unknown } | null | undefined)?.field;
  return {
    code: err.code,
    status: err.status,
    message: err.message,
    ...(typeof field === 'string' ? { field } : {}),
  };
}

/**
 * classifySyncError — minimal foreground drainer (coordinator finding #1,
 * task 6.11 adversarial-review fix; second adversarial review finding #4
 * made this table explicit and exhaustive per status/code). A FULL error
 * taxonomy (slice-7-owned operator-facing per-reason UX/copy) still lives in
 * Slice 7's own `sync-error-policy.ts` — this only decides the outbox
 * STATE-MACHINE outcome (retry | needs-auth | reject) so items never get
 * silently stuck.
 *
 * RETRY (back to pending, with backoff): network_error, 408, 429, 5xx, any
 * non-ApiError (assume transient rather than silently reject on an
 * unrecognized shape), `photo_token_invalid` (the handler always requests a
 * FRESH presigned URL per attempt — see build-real-checkin-context.ts — so a
 * plain retry already re-uploads with a new token; no special-casing needed
 * here), and 403 `subscription_access_blocked` (kept pending — NOT
 * terminal — until slice 5's bypass/org decision is known; the distinct
 * `error.code` on the item is what lets /pending show a dedicated "blocked
 * by subscription" message instead of a generic retry spinner).
 *
 * NEEDS-AUTH: 401 (session expired — the drainer stops draining until a
 * same-org login/session-restore moves the item back to pending, see
 * foreground-drainer.ts's needs-auth recovery step).
 *
 * REJECT (terminal, needs explicit operator action from /pending): 409
 * (duplicate_active_ticket, qr_tag_conflict, idempotency_key_reused — all
 * distinguishable by `error.code` for a future force/retry action), 422
 * (validation), 403 location-denial codes (LOCATION_ACCESS_DENIED /
 * LOCATION_INACTIVE — rejected for THIS item only; deliberately never calls
 * `handleLocationAccessDenied` itself, see the `suppressLocationAccessHandling`
 * option threaded through `createTicket`/`request()`), 404 (location
 * missing/deleted since capture).
 *
 * Incident photos (third adversarial review finding H3 — the previous
 * paragraph here was WRONG the moment it was written: it assumed incident
 * photo keys would always be server tokens obtained at CAPTURE time,
 * carried unchanged through `item.payload`, so a late sync could 422 with a
 * PURGED token that retrying could never fix. That assumption never
 * actually shipped — H3 fixed it at the SOURCE instead: incident photos are
 * now local blobs too (`item.incidentPhotoRefs`, `LocalIncidentPhotoSink`),
 * uploaded fresh via `requestIncidentPhotoUploadUrls` on every sync attempt,
 * exactly like the 4 mandatory angles). A `photo_token_invalid` for an
 * incident key is therefore classified IDENTICALLY to a mandatory-angle one
 * — RETRY, already covered by the branch above — since a plain retry
 * re-requests a fresh incident upload URL too. No incident-specific branch
 * needed HERE.
 *
 * Fourth adversarial review finding HIGH #1 — `checkin-create.handler.ts`
 * ALSO calls this function directly, for a second, narrower purpose:
 * `incident-photo-upload-urls` is gated behind `PlanFeatureGuard
 * ('damageReports')` on the backend, so a plan without that feature (or one
 * downgraded between capture and sync) 403s with `feature_not_in_plan` —
 * this classifier's existing `reject` bucket already covers that code
 * correctly (see the REJECT paragraph above, "Any other 4xx not enumerated
 * above"). The handler uses that SAME `reject` classification to decide
 * "drop the incident photos and still create the ticket" instead of
 * "reject the whole item" — RETRY/NEEDS-AUTH classifications there are
 * re-thrown unchanged (an item must never silently drop evidence because of
 * a transient network blip or an expired session).
 */
export function classifySyncError(err: unknown): SyncErrorClassification {
  if (err instanceof DuplicateTicketApiError) {
    return { kind: 'reject', error: toLastError(err) };
  }

  if (err instanceof ApiError) {
    const code = String(err.code);
    const status = err.status;

    if (status === 401) {
      return { kind: 'needs-auth', error: toLastError(err) };
    }

    if (
      code === 'network_error' ||
      status === 408 ||
      status === 429 ||
      (status !== undefined && status >= 500) ||
      code === 'photo_token_invalid' ||
      (status === 403 && code === SUBSCRIPTION_BLOCKED_CODE)
    ) {
      return { kind: 'retry', error: toLastError(err) };
    }

    if (status === 403 && LOCATION_DENIED_CODES.has(code)) {
      return { kind: 'reject', error: toLastError(err) };
    }

    if (status === 404 || status === 409 || status === 422) {
      return { kind: 'reject', error: toLastError(err) };
    }

    // Any other 4xx not enumerated above — stop retrying forever rather than
    // loop indefinitely on a shape we don't recognize; surfaced in /pending.
    return { kind: 'reject', error: toLastError(err) };
  }

  // A thrown plain Error, a TypeError from fetch itself, an AbortError from
  // the sync timeout (finding #5) — all assumed transient.
  return {
    kind: 'retry',
    error: { code: 'unknown', message: err instanceof Error ? err.message : String(err) },
  };
}
